import { Footer, Header } from '@digital-retail/falabella-ui-cross-components';
import {
  getCommerceHeaders,
  isSodimacRetail,
  isSodimacStandalone,
  isStandalone,
  isTottusStandalone,
  QTY_LIMITS_KEY,
  updateCartQtyLimits,
} from '@digital-retail/store-manager';
import Router from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import Loader from '../../components/Loader/Loader';
import apiConfig from '../../config/api/apiConfig';
import constants from '../../config/constants';
import { CookieStorage } from '../../utils/CookieStorage';
import { httpService } from '../../utils/httpService';
import _ from '../../utils/LodashImports';
import { navigateWithUrl, redirect } from '../../utils/RouteHandler';
import { isMarketPlace } from '../../utils/urlHelper';
import { useUser } from '../../utils/UserContext';
import { WebStorage } from '../../utils/WebStorage';
import WithUrl from '../../utils/WithUrl';
import { layoutStyle } from './Layout.style';

const { DEVICE_DESKTOP } = constants;

const LayoutContainer = ({ children, appCtx }) => {
  const layoutCss = layoutStyle(appCtx.tenant);
  const [cartCount, setCartCount] = useState('');
  const { refreshUserData, setTick, tick } = useUser();
  const isTottusSA = isTottusStandalone(appCtx.store);
  const isSodimacSA = isSodimacStandalone(appCtx.store);
  const isSodimacRe = isSodimacRetail(appCtx.store);

  const syncCartQuantities = async (count) => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!count || count === 0) {
      localStorage.setItem(QTY_LIMITS_KEY, '{}');
      window.dispatchEvent(new Event('cart:qtyUpdated'));
      setTick(Date.now());
      return;
    }

    const { regionCode, pid, latLong, deviceType, store } = appCtx;
    const cartId = CookieStorage.getCookie('cartId');

    if (!cartId) {
      window.dispatchEvent(new Event('cart:qtyUpdated'));
      setTick(Date.now());
      return;
    }

    const mkp = isMarketPlace();
    const headers = getCommerceHeaders({ isMarketPlace: mkp, regionCode, store });

    const response = await httpService(appCtx).get(apiConfig.getCartCount(cartId, regionCode, pid, latLong), {
      headers: {
        ...headers,
        'x-channel-id': deviceType === DEVICE_DESKTOP ? 'WEB' : 'MOBILE',
      },
    });

    if (isTottusSA && response.statusCode === 200) {
      updateCartQtyLimits(response);
    }

    window.dispatchEvent(new Event('cart:qtyUpdated'));
    setTick(Date.now());
  };

  useEffect(() => {
    if (appCtx.USER_DATA_COOKIE) {
      const userDetails = appCtx.USER_DATA_COOKIE.split('*');
      const newCartCount = userDetails[1];
      setCartCount(newCartCount);

      if (isTottusSA) {
        syncCartQuantities(parseInt(newCartCount || 0, 10));
      }
    }
  }, [appCtx.USER_DATA_COOKIE]);

  useEffect(() => {
    const handlePageShow = () => {
      if (isTottusSA && tick === 0) {
        const dataCookie = CookieStorage.getCookie('CATALYST_DATA_COOKIE');
        const count = parseInt(dataCookie?.split?.('*')?.[1] || 0, 10);

        syncCartQuantities(count);
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const currentLocation = _.get(appCtx, 'zoneName', '');

  const atgBaseUrl = _.get(appCtx, 'siteConfig.endpoints.ATG_AUTH_BASEURL', 'https://www.falabella.com');
  const catalystBaseUrl = _.get(
    appCtx,
    'header.entry.header_configs[0].links.catalystbaseurl',
    'https://www.falabella.com'
  );

  const transversalBannerConfig = JSON.parse(_.get(appCtx, 'siteConfig.toggles.transversalBannerConfig', '{}'));

  const { deviceType } = appCtx;
  const isMobile = deviceType === constants.DEVICE_MOBILE;

  const disableGeofinderModalForMobile = appCtx?.siteConfig?.toggles?.disableGeofinderModalForMobile && isMobile;

  const handleRouteChange = (termo, searchInSis) => {
    const page = isStandalone({ store: appCtx.store }) ? 'SA_SEARCH_PAGE' : 'SEARCH_PAGE';
    const urlParams = {
      custom: {
        searchTerm: termo,
      },
    };

    if ((appCtx.store && searchInSis) || !appCtx.store) {
      if (Router.router.route !== '/search') {
        const url = WithUrl(page, appCtx.regionCode, urlParams, appCtx.store);
        redirect(url);
      } else {
        navigateWithUrl({
          rootUrl: isStandalone({ store: appCtx.store }) ? '/buscar' : '/search',
          site: appCtx.tenant,
          url: `Ntt=${termo}`,
          store: appCtx.store,
          regionCode: appCtx.regionCode,
          onRouteChange: () => {
            WebStorage.setItem({
              name: constants.SESSION_STORAGE_NEW_PAGE,
              shouldPersist: false,
              value: 'true',
            });
            appCtx.setBusy(true);
          },
        });
      }
    } else {
      const url = WithUrl(page, appCtx.regionCode, urlParams, appCtx.store);
      redirect(url);
    }
  };

  const onUserLogin = () => {
    refreshUserData();
  };
  const getFooterData = () => {
    const rawFooterData = _.get(appCtx, 'footer');
    if (!rawFooterData) return rawFooterData;
    return {
      ...rawFooterData,
      entry: {
        ...rawFooterData?.entry,
        bottom_banner: {
          // eslint-disable-next-line camelcase
          ...rawFooterData?.entry?.bottom_banner,
          enabled: false,
        },
      },
    };
  };

  return (
    <>
      <Header
        currentLocation={currentLocation}
        data={{
          header: {
            ..._.get(appCtx, 'header'),
            transversalBannerHtml: _.get(appCtx, 'transversalBanner'),
          },
          sisNavigationMenu: _.get(appCtx, 'sisNavigationMenu'),
          taxonomy: _.get(appCtx, 'taxonomy'),
        }}
        isGeoLocationEnabled={_.get(appCtx, 'siteConfig.toggles.isGeoLocationEnabled', false)}
        isTypeAheadEnabled={_.get(appCtx, 'siteConfig.toggles.isTypeAheadEnabled', false)}
        isATGLoginEnabled={_.get(appCtx, 'siteConfig.toggles.isATGLoginEnabled', false)}
        isDefaultComuna={_.get(appCtx, 'isDefaultComuna', false)}
        selectedZone={_.get(appCtx, 'comuna.data', undefined)}
        onUserLogout={appCtx.logoutUser}
        onUserLogin={onUserLogin}
        isOrangeHeader={appCtx.isOrangeHeader}
        isSISLevel2Header={appCtx.isSISLevel2Header}
        enableAnalyticsInfoCall
        isZoneModalEnabled
        userCartCount={cartCount}
        parentRouter={Router}
        handleRouteChange={handleRouteChange}
        catalystBaseUrl={catalystBaseUrl}
        atgBaseUrl={atgBaseUrl}
        store={appCtx.store}
        handleSoftRefresh={appCtx.reloadOnZoneChange}
        isPopOverVisible={!disableGeofinderModalForMobile}
        isWebPushNotificationEnabled={_.get(appCtx, 'siteConfig.toggles.isWebPushNotificationEnabled', false)}
        transversalBannerConfig={transversalBannerConfig}
        locationPathname={appCtx.url}
        isGoogleTagManagerEnabled
        isSodimacStandAloneSite={isSodimacSA || isSodimacRe}
        isTottusStandAloneSite={isTottusSA}
        hideZoneModalWebMobilePDP={_.get(appCtx, 'siteConfig.toggles.hideZoneModalWebMobilePDP', false)}
      />
      <div className="main">{children}</div>
      {appCtx.busy > 0 && <Loader />}
      <Footer data={getFooterData()} store={appCtx.store} />
      <style jsx>{layoutCss}</style>
    </>
  );
};

LayoutContainer.propTypes = {
  children: PropTypes.node,
  appCtx: PropTypes.object.isRequired,
};

LayoutContainer.defaultProps = {
  children: null,
};

export default LayoutContainer;
